import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import {
  FileText,
  User,
  Article,
  Robot,
  Copyright,
  Warning,
  Link as LinkIcon,
  Prohibit,
  IdentificationCard,
  CreditCard,
  XCircle,
  ArrowRight,
  CaretRight,
} from "@phosphor-icons/react";

// ─── Last updated ─────────────────────────────────────────────────────────────
const LAST_UPDATED = "May 27, 2026";
const EFFECTIVE_DATE = "May 27, 2026";
const CONTACT_EMAIL = "legal@trendming.com";
const SITE_URL = "https://trendming.com";

// ─── Structured data ──────────────────────────────────────────────────────────
const TERMS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Terms & Conditions — TrendMing",
  description:
    "Read TrendMing's Terms & Conditions governing use of our AI-powered news and content platform.",
  url: `${SITE_URL}/terms`,
  dateModified: "2026-05-27",
  isPartOf: {
    "@type": "WebSite",
    name: "TrendMing",
    url: SITE_URL,
  },
};

// ─── Section registry ─────────────────────────────────────────────────────────
const SECTIONS = [
  { id: "acceptance",      icon: FileText,           label: "Acceptance of Terms" },
  { id: "user-responsibilities", icon: User,          label: "User Responsibilities" },
  { id: "content-usage",   icon: Article,            label: "Content Usage Policy" },
  { id: "ai-disclaimer",   icon: Robot,              label: "AI Content Disclaimer" },
  { id: "ip",              icon: Copyright,          label: "Intellectual Property" },
  { id: "liability",       icon: Warning,            label: "Limitation of Liability" },
  { id: "external-links",  icon: LinkIcon,           label: "External Links" },
  { id: "prohibited",      icon: Prohibit,           label: "Prohibited Activities" },
  { id: "accounts",        icon: IdentificationCard, label: "Account Policy" },
  { id: "payments",        icon: CreditCard,         label: "Subscription & Payments" },
  { id: "termination",     icon: XCircle,            label: "Termination" },
];

// ─── Reusable sub-components ──────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">
      {children}
    </span>
  );
}

/** Renders an individual clause block — heading + body content */
function Clause({ id, icon: Icon, label, number, children }) {
  return (
    <section id={id} aria-labelledby={`${id}-heading`} className="scroll-mt-24">
      <div className="flex items-start gap-5 mb-5">
        <div className="w-10 h-10 border border-slate-200 bg-white flex items-center justify-center shrink-0 mt-0.5 rounded-lg">
          <Icon size={18} weight="duotone" className="text-blue-600" aria-hidden="true" />
        </div>
        <div>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            {String(number).padStart(2, "0")}
          </span>
          <h2
            id={`${id}-heading`}
            className="font-bold text-2xl md:text-3xl leading-tight mt-0.5 text-slate-900"
          >
            {label}
          </h2>
        </div>
      </div>
      <div className="pl-0 md:pl-15 space-y-4 text-slate-600 text-base leading-relaxed">
        {children}
      </div>
    </section>
  );
}

/** Renders a highlighted callout block */
function Callout({ type = "info", children }) {
  const styles = {
    info:    "border-slate-200 bg-slate-50 text-slate-700",
    warning: "border-yellow-200 bg-yellow-50 text-yellow-800",
    danger:  "border-red-200 bg-red-50 text-red-700",
  };
  return (
    <div className={`border p-4 text-sm leading-relaxed rounded-lg ${styles[type]}`}>
      {children}
    </div>
  );
}

/** Renders an unordered list with blue bullet markers */
function TermsList({ items }) {
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3">
          <CaretRight
            size={12}
            weight="bold"
            className="text-blue-600 mt-1.5 shrink-0"
            aria-hidden="true"
          />
          <span className="text-slate-600 text-sm leading-relaxed">{item}</span>
        </li>
      ))}
    </ul>
  );
}

/** Sticky sidebar table of contents */
function TableOfContents({ activeId }) {
  return (
    <nav aria-label="Table of contents" className="sticky top-24 hidden xl:block">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-4">
        Contents
      </p>
      <ul className="space-y-0 border border-slate-200 divide-y divide-slate-200 rounded-xl overflow-hidden">
        {SECTIONS.map(({ id, label, icon: Icon }) => {
          const active = activeId === id;
          return (
            <li key={id}>
              <a
                href={`#${id}`}
                className={`flex items-center gap-3 px-4 py-3 text-xs transition-colors group ${
                  active
                    ? "bg-blue-50 text-blue-600 border-l-2 border-blue-600"
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                }`}
              >
                <Icon
                  size={13}
                  weight="duotone"
                  className={active ? "text-blue-600" : "text-slate-400 group-hover:text-slate-500"}
                  aria-hidden="true"
                />
                <span className="tracking-wide leading-snug">{label}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function TermsPage() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const observerRef = useRef(null);

  useEffect(() => {
    const targets = SECTIONS.map(({ id }) => document.getElementById(id)).filter(Boolean);
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );
    targets.forEach((el) => observerRef.current.observe(el));
    return () => observerRef.current?.disconnect();
  }, []);

  return (
    <>
      <SEO
        title="Terms & Conditions | TrendMing"
        description="Read TrendMing's Terms & Conditions. Understand the rules governing use of our AI-powered news aggregation, content generation, and trend intelligence platform."
        canonical={`${SITE_URL}/terms`}
        ogType="website"
        structuredData={[TERMS_SCHEMA]}
      />

      <main className="max-w-[1200px] mx-auto px-4 md:px-8 py-12 md:py-20">

        {/* ── Page header ─────────────────────────────────────────────────── */}
        <header className="mb-12 md:mb-16">
          <SectionLabel>/ Legal</SectionLabel>
          <h1 className="font-bold text-4xl md:text-6xl mt-4 mb-5 leading-[1.05] text-slate-900">
            Terms &amp; Conditions
          </h1>
          <p className="text-slate-500 text-base max-w-2xl leading-relaxed mb-8">
            These Terms govern your access to and use of TrendMing's platform, services, and
            AI-powered content tools. Please read them carefully before using the site.
          </p>

          {/* Meta strip */}
          <div className="flex flex-wrap gap-px bg-slate-200 border border-slate-200 w-fit rounded-lg overflow-hidden">
            {[
              { key: "Last updated", val: LAST_UPDATED },
              { key: "Effective", val: EFFECTIVE_DATE },
              { key: "Jurisdiction", val: "Global" },
            ].map(({ key, val }) => (
              <div key={key} className="bg-white px-5 py-3">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{key}</p>
                <p className="text-xs text-slate-900 mt-0.5 font-medium">{val}</p>
              </div>
            ))}
          </div>
        </header>

        {/* ── Two-column layout: content + sidebar ToC ────────────────────── */}
        <div className="flex gap-12 xl:gap-20 items-start">

          {/* ── Main content ───────────────────────────────────────────────── */}
          <div className="flex-1 min-w-0 space-y-16">

            {/* 01 — Acceptance */}
            <Clause id="acceptance" icon={FileText} label="Acceptance of Terms" number={1}>
              <p>
                By accessing or using TrendMing ("Platform," "we," "us," or "our") — including
                visiting <strong className="text-slate-900">trendming.com</strong>, creating an account,
                subscribing to a plan, or consuming any content or service we provide — you
                ("User," "you") agree to be legally bound by these Terms &amp; Conditions ("Terms").
              </p>
              <p>
                If you do not agree with any part of these Terms, you must discontinue use of the
                Platform immediately. Your continued use following any update to these Terms
                constitutes acceptance of the revised version.
              </p>
              <Callout type="info">
                <strong className="text-slate-900 font-semibold">Age requirement:</strong> You must be
                at least 13 years of age (or the minimum digital age of consent in your jurisdiction,
                whichever is higher) to use TrendMing. By using this Platform, you represent that
                you meet this requirement.
              </Callout>
              <p>
                These Terms apply to all visitors, registered users, and subscribers regardless
                of geographic location. Where local law conflicts with these Terms, applicable
                local law will govern only to the extent required.
              </p>
            </Clause>

            <div className="border-t border-slate-200" />

            {/* 02 — User Responsibilities */}
            <Clause id="user-responsibilities" icon={User} label="User Responsibilities" number={2}>
              <p>
                When using TrendMing, you are responsible for ensuring your conduct meets the
                following standards. You agree to:
              </p>
              <TermsList items={[
                "Provide accurate, current, and complete information when creating an account or interacting with the Platform.",
                "Maintain the security and confidentiality of your login credentials and notify us immediately at " + CONTACT_EMAIL + " if you suspect unauthorised access.",
                "Use the Platform only for lawful, personal, or professional informational purposes.",
                "Respect the intellectual property rights of TrendMing, its partners, and third-party content providers.",
                "Refrain from misrepresenting yourself, impersonating others, or creating false identities.",
                "Comply with all applicable local, national, and international laws and regulations.",
                "Not interfere with or disrupt the integrity or performance of the Platform or its associated systems.",
                "Report errors, bugs, or potential security vulnerabilities responsibly through our official channels.",
              ]} />
              <Callout type="warning">
                Failure to comply with your responsibilities may result in suspension or permanent
                termination of your account without prior notice, at our sole discretion.
              </Callout>
            </Clause>

            <div className="border-t border-slate-200" />

            {/* 03 — Content Usage Policy */}
            <Clause id="content-usage" icon={Article} label="Content Usage Policy" number={3}>
              <p>
                TrendMing aggregates content from a wide range of third-party news sources,
                publishers, and wire services. All content displayed on the Platform is subject
                to the following usage conditions:
              </p>
              <TermsList items={[
                "Content displayed on TrendMing is provided for personal, non-commercial informational use only.",
                "Reproducing, redistributing, publishing, or commercially exploiting aggregated content without prior written consent from TrendMing and/or the original content owner is strictly prohibited.",
                "AI-generated summaries, briefs, and content kits produced by our tools are licensed to you for personal and professional use but may not be resold or sublicensed.",
                "Proper attribution must be maintained when sharing or citing content sourced from TrendMing.",
                "Caching, scraping, or systematically downloading content in bulk — by automated means or otherwise — is not permitted without an explicit API agreement.",
                "Content accessed through premium subscription features is subject to additional licensing terms outlined in your plan.",
              ]} />
              <p>
                TrendMing makes reasonable efforts to ensure content accuracy but does not
                guarantee the completeness, correctness, or timeliness of any information on
                the Platform. Users are encouraged to verify critical information through
                primary sources before acting upon it.
              </p>
              <Callout type="info">
                <strong className="text-slate-900 font-semibold">Fair use:</strong> Sharing short
                excerpts with attribution for commentary, criticism, or educational purposes
                is permitted under fair use/fair dealing provisions of applicable copyright law.
              </Callout>
            </Clause>

            <div className="border-t border-slate-200" />

            {/* 04 — AI Content Disclaimer */}
            <Clause id="ai-disclaimer" icon={Robot} label="AI-Generated Content Disclaimer" number={4}>
              <p>
                TrendMing uses artificial intelligence and machine learning technologies to
                summarise, analyse, cluster, and generate content derived from source articles.
                By using AI-powered features you acknowledge and accept the following:
              </p>
              <TermsList items={[
                "AI-generated summaries and content kits are produced algorithmically and have not been reviewed by a human editor prior to delivery.",
                "AI outputs may contain inaccuracies, omissions, biases inherited from training data, or factual errors. They should not be used as the sole basis for medical, legal, financial, or safety-critical decisions.",
                "Summaries are derivative works intended to convey the gist of source material. They do not replace reading the original article and may not capture all nuance.",
                "TrendMing does not warrant that AI-generated content is free from errors. We are not liable for any reliance placed on such content.",
                "Content generated using our AI tools may be similar to outputs generated for other users querying the same source material.",
                "We continuously update our AI models, which may result in changes to output style, length, or quality over time.",
              ]} />
              <Callout type="danger">
                <strong className="font-semibold">Important:</strong> AI-generated content is
                not professional advice. Nothing on TrendMing constitutes financial, legal,
                medical, or investment advice. Always consult a qualified professional for
                decisions in these domains.
              </Callout>
            </Clause>

            <div className="border-t border-slate-200" />

            {/* 05 — Intellectual Property */}
            <Clause id="ip" icon={Copyright} label="Intellectual Property" number={5}>
              <p>
                All original materials on TrendMing — including but not limited to software,
                interface design, typography, layout, logos, trademarks, AI models, proprietary
                algorithms, and original written content — are the exclusive intellectual property
                of TrendMing or its licensors.
              </p>
              <TermsList items={[
                "The TrendMing name, logo, and brand identity are registered or common-law trademarks. Unauthorised use is prohibited.",
                "Third-party content aggregated on the Platform remains the intellectual property of its respective creators and publishers.",
                "You may not copy, modify, distribute, sell, or create derivative works from TrendMing's proprietary materials without express written permission.",
                "Feedback, suggestions, or ideas you submit about the Platform may be used by TrendMing without restriction or compensation.",
                "Any content you create using TrendMing's AI tools remains yours, subject to the content usage licence granted to TrendMing to deliver the service.",
                "Scraping, reverse engineering, or decompiling any part of the Platform is strictly prohibited.",
              ]} />
              <p>
                If you believe that your intellectual property rights have been infringed on
                our Platform, please contact us at{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">
                  {CONTACT_EMAIL}
                </a>{" "}
                with a detailed DMCA-compliant notice.
              </p>
            </Clause>

            <div className="border-t border-slate-200" />

            {/* 06 — Limitation of Liability */}
            <Clause id="liability" icon={Warning} label="Limitation of Liability" number={6}>
              <p>
                To the fullest extent permitted by applicable law, TrendMing and its officers,
                directors, employees, partners, and agents shall not be liable for:
              </p>
              <TermsList items={[
                "Any direct, indirect, incidental, special, consequential, or exemplary damages arising from your use of (or inability to use) the Platform.",
                "Loss of profits, data, goodwill, or other intangible losses resulting from reliance on content or services provided.",
                "Errors, inaccuracies, or omissions in any content, including AI-generated summaries and aggregated news.",
                "Unauthorised access to or alteration of your account data or transmissions.",
                "Third-party conduct, content, or services accessible via or referenced by the Platform.",
                "Service interruptions, downtime, or data loss due to technical failures, maintenance, or force majeure events.",
              ]} />
              <Callout type="warning">
                In jurisdictions that do not allow the exclusion of certain warranties or limitation
                of liability for consequential or incidental damages, TrendMing's liability is
                limited to the maximum extent permitted by law. Our total aggregate liability shall
                not exceed the greater of (a) the amount you paid us in the 12 months preceding
                the claim, or (b) USD $50.
              </Callout>
              <p>
                TrendMing provides the Platform on an "as is" and "as available" basis, without
                warranties of any kind, either express or implied, including but not limited to
                implied warranties of merchantability, fitness for a particular purpose,
                and non-infringement.
              </p>
            </Clause>

            <div className="border-t border-slate-200" />

            {/* 07 — External Links */}
            <Clause id="external-links" icon={LinkIcon} label="External Links Disclaimer" number={7}>
              <p>
                TrendMing aggregates content from and may link to third-party websites, news
                publishers, social media platforms, and external services. The following applies:
              </p>
              <TermsList items={[
                "External links are provided for convenience and informational purposes only. We do not endorse or take responsibility for the content, privacy practices, or accuracy of any linked third-party site.",
                "Clicking on third-party links will take you away from TrendMing. We have no control over and assume no responsibility for the content or availability of external sites.",
                "The presence of a link on TrendMing does not imply endorsement, sponsorship, or affiliation with the linked entity.",
                "Third-party sites may have their own privacy policies and terms of service, which we encourage you to review.",
                "TrendMing is not responsible for any harm, loss, or damage arising from your interaction with third-party sites or services.",
              ]} />
            </Clause>

            <div className="border-t border-slate-200" />

            {/* 08 — Prohibited Activities */}
            <Clause id="prohibited" icon={Prohibit} label="Prohibited Activities" number={8}>
              <p>
                To protect the integrity of the Platform and the experience of all users, the
                following activities are strictly prohibited:
              </p>
              <TermsList items={[
                "Using the Platform for any unlawful purpose or in violation of any applicable laws or regulations.",
                "Attempting to gain unauthorised access to any portion of the Platform, servers, databases, or connected systems.",
                "Uploading, transmitting, or distributing malware, viruses, or any code designed to disrupt, damage, or limit the functionality of the Platform.",
                "Using automated bots, crawlers, or scrapers to harvest content, data, or user information without authorisation.",
                "Harassing, abusing, threatening, or impersonating any person or entity through any feature of the Platform.",
                "Circumventing, disabling, or interfering with security-related features or access controls.",
                "Using the Platform to distribute spam, unsolicited commercial communications, or phishing attempts.",
                "Misrepresenting the origin of content or falsely claiming AI-generated content as your own original work when it clearly is not.",
                "Attempting to reverse-engineer, decompile, or extract the source code of our AI models or platform software.",
                "Using TrendMing content to train competing AI models or for bulk automated dataset creation.",
                "Engaging in market manipulation or spreading deliberate misinformation using our content tools.",
              ]} />
              <Callout type="danger">
                Violations may result in immediate account suspension, permanent banning, legal
                action, and/or referral to law enforcement authorities depending on the severity
                of the breach.
              </Callout>
            </Clause>

            <div className="border-t border-slate-200" />

            {/* 09 — Account Policy */}
            <Clause id="accounts" icon={IdentificationCard} label="Account Usage Policy" number={9}>
              <p>
                Creating and maintaining a TrendMing account is subject to the following conditions:
              </p>
              <TermsList items={[
                "One account per person. Creating multiple accounts to circumvent usage limits or subscription restrictions is prohibited.",
                "You are solely responsible for all activities that occur under your account and credentials.",
                "You must notify us immediately of any suspected breach or unauthorised use of your account.",
                "Sharing account credentials with third parties, or using another person's account without permission, is not allowed.",
                "TrendMing reserves the right to refuse account creation, suspend, or delete accounts at our discretion.",
                "Accounts inactive for more than 24 consecutive months may be deactivated. We will attempt to notify you prior to deactivation.",
                "Upon account deletion, your personal data will be handled in accordance with our Privacy Policy.",
              ]} />
              <p>
                You may delete your account at any time from your Profile Settings. Deletion is
                subject to a 30-day grace period during which you may reactivate your account.
                After 30 days, deletion is permanent and irreversible.
              </p>
            </Clause>

            <div className="border-t border-slate-200" />

            {/* 10 — Subscription & Payments */}
            <Clause id="payments" icon={CreditCard} label="Subscription & Payment Terms" number={10}>
              <p>
                TrendMing offers a free tier and optional paid subscription plans ("Premium").
                The following terms govern all commercial transactions on the Platform:
              </p>
              <TermsList items={[
                "Premium subscriptions are billed on a recurring basis (monthly or annual) as selected at checkout.",
                "All prices are displayed in US Dollars (USD) and are inclusive of applicable taxes where required by law.",
                "Payment is processed securely by our third-party payment provider (Stripe or Razorpay). TrendMing does not store your full payment details.",
                "Subscriptions renew automatically unless cancelled at least 24 hours before the renewal date.",
                "You may cancel your subscription at any time from your Profile Settings. Cancellation takes effect at the end of the current billing period; no refunds are issued for unused time within a paid period.",
                "Refund requests will be considered on a case-by-case basis for technical issues preventing service delivery. Contact us at " + CONTACT_EMAIL + " within 7 days of the charge.",
                "TrendMing reserves the right to change subscription pricing with at least 30 days' advance notice to affected subscribers.",
                "Free trial offers, promotional pricing, and discount codes are subject to additional terms stated at the point of offer.",
                "Failed payments may result in downgrade to the free tier. We will attempt to notify you before access is restricted.",
              ]} />
              <Callout type="info">
                <strong className="text-slate-900 font-semibold">Free tier:</strong> The free plan
                is available indefinitely but is subject to usage limits that may change over
                time. We will communicate material changes to free-tier limits with reasonable
                advance notice.
              </Callout>
            </Clause>

            <div className="border-t border-slate-200" />

            {/* 11 — Termination */}
            <Clause id="termination" icon={XCircle} label="Termination Policy" number={11}>
              <p>
                Either party may terminate the user relationship at any time and for any reason,
                subject to the following terms:
              </p>
              <TermsList items={[
                "You may terminate your account at any time by deleting it through your Profile Settings.",
                "TrendMing may suspend or terminate your access immediately, without prior notice or liability, for any breach of these Terms.",
                "Upon termination, your right to use the Platform ceases immediately. Active paid subscriptions will not be refunded unless required by law.",
                "Provisions of these Terms that by their nature should survive termination — including intellectual property rights, disclaimers, liability limitations, and dispute resolution — will remain in effect.",
                "TrendMing reserves the right to permanently delete your account data after the applicable retention period described in our Privacy Policy.",
                "We may discontinue, modify, or restrict any aspect of the Platform at any time. Material changes to services offered under paid plans will be communicated with at least 30 days' notice.",
              ]} />
              <p>
                TrendMing will make reasonable efforts to provide advance notice of service
                discontinuation. In the event of Platform shutdown, active paid subscribers will
                receive a pro-rata refund for unused subscription days.
              </p>
            </Clause>

            <div className="border-t border-slate-200" />

            {/* Governing Law */}
            <section aria-labelledby="governing-law-heading" className="scroll-mt-24">
              <h2 id="governing-law-heading" className="font-bold text-2xl md:text-3xl mb-4 text-slate-900">
                Governing Law &amp; Disputes
              </h2>
              <div className="space-y-4 text-slate-600 text-base leading-relaxed">
                <p>
                  These Terms shall be governed by and construed in accordance with applicable
                  international commercial law principles. Any disputes arising from or relating
                  to these Terms or your use of the Platform shall first be addressed through
                  good-faith negotiation.
                </p>
                <p>
                  If a dispute cannot be resolved informally within 30 days, either party may
                  pursue binding arbitration or, where arbitration is not legally enforceable,
                  submit the dispute to the competent courts of the jurisdiction in which TrendMing
                  is incorporated.
                </p>
              </div>
            </section>

            <div className="border-t border-slate-200" />

            {/* Changes to Terms */}
            <section aria-labelledby="changes-heading" className="scroll-mt-24">
              <h2 id="changes-heading" className="font-bold text-2xl md:text-3xl mb-4 text-slate-900">
                Changes to These Terms
              </h2>
              <div className="space-y-4 text-slate-600 text-base leading-relaxed">
                <p>
                  TrendMing reserves the right to update or modify these Terms at any time.
                  When we make material changes, we will update the "Last Updated" date at the
                  top of this page and, where appropriate, notify registered users via email
                  or an in-platform notification.
                </p>
                <p>
                  Your continued use of the Platform after changes are posted constitutes your
                  acceptance of the revised Terms. If you disagree with any changes, you must
                  stop using the Platform and may delete your account.
                </p>
              </div>
            </section>

            <div className="border-t border-slate-200" />

            {/* Contact */}
            <section aria-labelledby="contact-heading" className="scroll-mt-24">
              <h2 id="contact-heading" className="font-bold text-2xl md:text-3xl mb-4 text-slate-900">
                Contact Us
              </h2>
              <p className="text-slate-600 text-base leading-relaxed mb-6">
                For questions, legal enquiries, DMCA notices, or concerns about these Terms, please
                reach out to our team:
              </p>
              <div className="border border-slate-200 bg-slate-50 p-6 md:p-8 space-y-2 rounded-xl">
                <p className="text-sm text-slate-600">
                  <span className="text-slate-400 text-xs uppercase tracking-wider mr-3">Email</span>
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">
                    {CONTACT_EMAIL}
                  </a>
                </p>
                <p className="text-sm text-slate-600">
                  <span className="text-slate-400 text-xs uppercase tracking-wider mr-3">Web</span>
                  <a href={SITE_URL} className="text-slate-600 hover:text-slate-900">
                    {SITE_URL}
                  </a>
                </p>
              </div>
            </section>

            {/* ── CTA ─────────────────────────────────────────────────────── */}
            <section
              aria-label="Get started"
              className="border border-blue-200 bg-blue-50 px-8 py-12 text-center rounded-2xl"
            >
              <SectionLabel>/ Ready to explore?</SectionLabel>
              <h2 className="font-bold text-3xl md:text-4xl mt-4 mb-4 text-slate-900">
                Start with TrendMing — free, forever
              </h2>
              <p className="text-slate-600 text-base max-w-lg mx-auto mb-8 leading-relaxed">
                Join thousands of professionals staying ahead of every global trend.
                No credit card required.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 text-sm rounded-xl transition-colors"
                >
                  Create free account <ArrowRight size={14} weight="bold" />
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold py-3 px-6 text-sm rounded-xl transition-colors"
                >
                  Learn about us
                </Link>
              </div>
            </section>

          </div>{/* end main content */}

          {/* ── Sidebar ToC ─────────────────────────────────────────────────── */}
          <aside className="w-64 shrink-0">
            <TableOfContents activeId={activeId} />
          </aside>

        </div>
      </main>
    </>
  );
}
